import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc-client/client";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoModalHeader } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import CircleSpinner from "@redotech/redo-web/circle-spinner.svg";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import * as styles from "./print-batch-modal.module.css";

export const DocumentPrintModal = memo(function DocumentPrintModal({
  title,
  subtitle,
  onClose,
  fetchPdfUrl,
  errorMessage,
}: {
  title: string;
  subtitle: string;
  onClose: () => void;
  fetchPdfUrl: (
    client: RedoOutboundLabelsRpcClient,
    signal: AbortSignal,
  ) => Promise<{ url: string }>;
  errorMessage?: string;
}) {
  const client = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  const printLoad = useLoad(
    async (signal) => {
      const result = await fetchPdfUrl(client, signal);
      return result;
    },
    [client, fetchPdfUrl],
  );

  return (
    <RedoBaseModal isOpen onModalCloseRequested={onClose}>
      <RedoModalHeader
        cancelClicked={onClose}
        subtitle={subtitle}
        title={title}
      />
      <Flex data-testid="document-print-modal" dir="column" gap="none">
        {printLoad.pending ? (
          <Flex align="center" className={styles.iframe} justify="center">
            <CircleSpinner />
            <Text>Generating PDF</Text>
          </Flex>
        ) : printLoad.error ? (
          <Flex
            align="center"
            className={styles.iframe}
            dir="column"
            gap="lg"
            justify="center"
            p="2xl"
          >
            <Text
              fontSize="lg"
              fontWeight="semibold"
              textAlign="center"
              textColor="error"
            >
              {errorMessage || "Failed to generate document"}
            </Text>
            <Text fontSize="sm" textAlign="center">
              {printLoad.error.message ||
                "Failed to generate the requested document. Please try again or contact support."}
            </Text>
          </Flex>
        ) : (
          <iframe className={styles.iframe} src={printLoad.value?.url} />
        )}
      </Flex>
      <Flex align="center" dir="row" justify="space-between" p="2xl">
        <Flex p="md">
          {!printLoad.pending && !printLoad.error && printLoad.value?.url && (
            <RedoExternalLink
              text="Open PDF in another tab"
              url={printLoad.value.url}
            />
          )}
        </Flex>
        <Flex dir="row" gap="md">
          <RedoButton
            hierarchy="secondary"
            onClick={onClose}
            size="xl"
            text="Close"
          />
        </Flex>
      </Flex>
    </RedoBaseModal>
  );
});

export const BarcodePrintModal = DocumentPrintModal;
